/**
 * LE CATALOGUE DES RUBRIQUES — une seule liste, trois lecteurs.
 *
 * ⚠️ IL EST DANS `src/partage/` POUR LA MÊME RAISON QUE `prefixes-api.ts` :
 * l'étage `interface` du Dockerfile ne copie que `frontend/` et `src/partage/`.
 * Un catalogue rangé dans `frontend/src/` compilerait ici et laisserait le
 * SERVICE incapable de contrôler ce que la base déclare.
 *
 * Les trois lecteurs :
 *   1. `frontend/src/app.tsx` — dessine le rail ;
 *   2. `src/modules/modes.ts` — refuse une rubrique inconnue déclarée en base ;
 *   3. `tests/modes.test.ts`  — confronte `sec.mode_rubrique` à cette liste.
 *
 * ⚠️ CHAQUE ENTRÉE DIT SON ÉTAT RÉEL : 30 rubriques, dont 29 bâties et 1 non
 * bâties. Afficher les non bâties comme si elles fonctionnaient produirait
 * exactement le défaut que ce projet combat : une façade qui ne distingue pas
 * « vide » de « pas encore construit ». Une rubrique non bâtie est affichée,
 * désactivée, et DIT ce qui lui manque.
 *
 * ⚠️ CES TROIS NOMBRES SONT GARDÉS PAR UN ESSAI, et ce n'est pas du zèle : la
 * phrase annonçait « dix-huit, dont DOUZE bâties » alors que la liste en
 * portait dix-neuf dont seize — un nombre TAPÉ dérive à chaque ajout, sans
 * qu'aucun test ne rougisse, et se recopie ensuite de bonne foi. Le garde vit
 * dans `tests/rubriques-icones.test.ts` : il relit CE commentaire et le
 * confronte à la liste.
 */
export type EtatRubrique = 'disponible' | 'non_construit';

export interface Rubrique {
  cle: string;
  libelle: string;
  /**
   * ⚠️ UNE CLÉ D'ICÔNE, PAS UN CARACTÈRE. Ce champ portait un emoji — rendu
   * par la police du système, donc d'un dessin, d'une couleur et d'une graisse
   * différents sur chaque machine, insensible à la couleur du texte et à
   * l'état du bouton. Sur un acte d'État, 🏛️ et 💰 portent en outre une charge
   * expressive qui n'a pas sa place. Le dessin vit dans
   * `frontend/src/icones.tsx` ; ce catalogue, lu aussi par le SERVICE, ne
   * connaît que son nom.
   */
  icone: string;
  etat: EtatRubrique;
  /** Permission de lecture exigée par le service pour cet écran. */
  ressource?: string;
  /**
   * ⚠️ QUAND « LIRE » NE SUFFIT PAS — l'atelier (D.968). Le rail ne testait
   * que l'opération `lire` : il ne savait pas dire « servie aux seuls
   * traceurs ». Quand ce champ est présent, l'entrée est active si l'agent
   * porte AU MOINS UNE de ces opérations sur la ressource — ce sont celles
   * que les routes du service exigent, jamais une étiquette de rôle.
   */
  operations?: readonly string[];
  /** Ce qu'il reste à faire, quand ce n'est pas bâti. */
  manque?: string;
}

export const RUBRIQUES: Rubrique[] = [
  /**
   * §3 · §4 · §83 — LE BUREAU. Fondateur : « le notaire arrive sur un cockpit,
   * pas sur une liste de formulaires ».
   *
   * ⚠️ IL EST EN TÊTE DU CATALOGUE PARCE QU'IL EST LE POINT D'ARRIVÉE. Le
   * `plan` répond « qu'est-ce qui m'attend, par catégorie » ; le bureau répond
   * « qu'est-ce qui BLOQUE, et quel est le geste suivant ». Les deux coexistent
   * et ne disent pas la même chose — l'un trie le travail, l'autre le déclenche.
   *
   * ⚠️ SANS `ressource` : le bureau se compose de ce que l'habilitation ouvre.
   * Un compteur fermé s'y affiche fermé, jamais à zéro.
   */
  { cle: 'bureau',      libelle: 'Bureau',           icone: 'bureau', etat: 'disponible' },
  // §37-§43 — le plan de travail du mode adopté. Sans `ressource` : chaque
  // tuile porte la sienne, et un plan entièrement masqué reste informatif —
  // il dit à l'agent ce que son métier suppose et que son habilitation n'ouvre pas.
  /* ⚠️ `plan` A ÉTÉ RETIRÉ DU CATALOGUE le 21/09/2026, et son contenu n'est
     PAS perdu : `PlanDeTravail` est monté ENCASTRÉ dans chaque bureau de
     métier. Décision du fondateur — « chaque mode est un métier […] chacun
     sera un OS » : un écran d'arrivée partagé par sept professions ne peut
     être l'OS d'aucune. Le retirer du catalogue sans le retirer des rails
     aurait fait rougir `rubriques-atteignables` ; migration 177 fait les deux
     dans le même geste. */
  // §31 — LE DOSSIER, placé juste après le plan de travail : c'est le point
  // d'entrée du guichet. Une demande d'usager commence ici, avant le levé et
  // avant tout acte — et c'est ce qui manquait pour qu'elle existe du tout.
  // §31 — LE GUICHET, avant le dossier : une demande entre par là, et le
  // dossier naît de sa validation. L'ordre du rail suit celui du travail.
  { cle: 'guichet',     libelle: 'Guichet',          icone: 'guichet',  etat: 'disponible', ressource: 'fon.demande' },
  // Vue de PILOTAGE : où le travail s'accumule. Ni la file (ce qui m'attend)
  // ni le dossier (où en est une affaire) ne répondent à cette question.
  { cle: 'flux',        libelle: 'Flux métier',      icone: 'flux',     etat: 'disponible', ressource: 'fon.demande' },
  /**
   * ⚠️ LE POSTE N'EST PAS « Dossiers ». `affaires` est la FILE — « par quoi je
   * commence » ; `instruction` ouvre UNE affaire et ne la quitte pas. Le
   * fondateur les a séparés explicitement le 24/09/2026 : « le bureau existe
   * pour tout le monde, le poste d'instruction est pour instruire une affaire ».
   */
  { cle: 'instruction', libelle: "Poste d'instruction", icone: 'instruction', etat: 'disponible', ressource: 'fon.affaire' },
  { cle: 'affaires',    libelle: 'Dossiers',         icone: 'affaires', etat: 'disponible', ressource: 'fon.affaire' },
  /**
   * ⚠️⚠️ « PLAN CADASTRAL » N'EST PLUS UNE RUBRIQUE — ELLE EST ENTRÉE DANS
   * CELLE-CI, le 22/09/2026. Elle dessinait les MÊMES sections que la carte,
   * au `<canvas>`, depuis une autre route, avec son propre texte
   * d'attribution ODbL écrit en dur là où la carte le CALCULE. Deux
   * représentations du même objet divergent au premier correctif appliqué à
   * une seule.
   *
   * Ce qui l'accompagnait n'est pas perdu : le tableau d'assemblage, la
   * statistique de la collectivité, la fiche de section, le découpage en
   * feuilles, les deux impressions et la liste des parcelles sont devenus les
   * panneaux de la carte (`frontend/src/panneau-cadastral.tsx`). Aucun n'est
   * une carte : ce sont un registre et un plan de tirage.
   *
   * ⚠️ ET DEUX MODES Y GAGNENT. `collectivite` et `etat` déclaraient `carte`
   * sans jamais déclarer `assemblage` : le plan cadastral leur était
   * inaccessible. La migration 210 retire les cinq lignes `assemblage` de
   * `sec.mode_rubrique`.
   */
  { cle: 'carte',       libelle: 'Carte cadastrale', icone: 'carte', etat: 'disponible', ressource: 'cad.parcelle' },
    /**
   * §7 — CAPACITÉ ET REPRÉSENTATION, placée JUSTE AVANT les mutations, et
   * l'ordre du rail dit la règle : on vérifie qui peut signer AVANT de dresser
   * l'acte. Placée après, elle serait une vérification d'après coup — c'est-
   * à-dire un constat d'annulabilité, pas un contrôle.
   *
   * ⚠️ CE N'EST PAS `comptes`. `comptes` gère les AGENTS du service ; celle-ci
   * regarde les PARTIES à l'acte — leur capacité propre, et qui agit pour
   * elles. Les confondre ferait chercher une tutelle dans la liste des agents.
   */
  { cle: 'capacite',    libelle: 'Capacité et pouvoirs', icone: 'capacite', etat: 'disponible', ressource: 'fon.capacite' },
{ cle: 'mutations',   libelle: 'Mutations',        icone: 'mutations', etat: 'disponible', ressource: 'fon.mutation' },
  /**
   * §17 · §50 à §53 — LA SUCCESSION, placée APRÈS les mutations et l'ordre
   * dit encore la règle : une succession ABOUTIT à une mutation, elle ne la
   * précède pas. Le partage se dépose comme acte une fois les vocations
   * arrêtées et les options exercées.
   *
   * ⚠️⚠️ ELLE N'EXISTAIT PAS ALORS QUE LE SERVICE ÉTAIT COMPLET. Mesuré le
   * 21/09/2026 par `tests/cahier-mutation.test.ts` : le module
   * `/successions` porte HUIT routes — ouverture, ayants droit, option,
   * vocation, masse, projet de partage — et **aucun écran ne les servait**.
   * Quatre écrans du cahier (§50 succession, §51 ayants droit, §52 actif
   * successoral, §53 partage) étaient donc jouables en HTTP et injouables par
   * un agent. C'est le motif écrit dans `dossier.tsx` : « un acte que seul un
   * ingénieur peut poser n'est pas un acte de l'administration ».
   */
  { cle: 'successions', libelle: 'Successions',      icone: 'successions', etat: 'disponible', ressource: 'fon.succession' },
  { cle: 'territoire',  libelle: 'Territoire',       icone: 'territoire', etat: 'disponible', ressource: 'ter.division' },
  { cle: 'foncier',     libelle: 'Foncier',          icone: 'foncier', etat: 'disponible', ressource: 'fon.droit' },
  /**
   * LOT B — L'ACTE COMMUNAL. Placé APRÈS Foncier et AVANT le Livre : c'est
   * l'ordre du parcours réel guinéen. Art.3 : la propriété se constate d'abord
   * au plan foncier de la commune (l'attestation de cession), et le titre
   * s'obtient « EN OUTRE » à la conservation. L'inverser dans le rail ferait
   * croire que le titre est la porte d'entrée — alors que la très grande
   * majorité des terrains n'y arrive jamais.
   */
  { cle: 'attestations', libelle: 'Attestations',     icone: 'attestation', etat: 'disponible',
    ressource: 'fon.attestation_cession' },
  /**
   * LOT C — LE LIVRE FONCIER. `ressource` est celle que le service exige
   * RÉELLEMENT (`exiger(..., 'fon.feuille_fonciere', 'lire')`) : la recopier de
   * mémoire ferait diverger l'écran et la garde, comme cela s'est déjà produit.
   */
  { cle: 'livre',       libelle: 'Livre foncier',    icone: 'livre', etat: 'disponible',
    ressource: 'fon.feuille_fonciere' },
  { cle: 'documents',   libelle: 'Documents',        icone: 'documents', etat: 'disponible', ressource: 'doc.document' },
  { cle: 'geometre',    libelle: 'Géomètre',         icone: 'geometre', etat: 'disponible', ressource: 'cad.releve' },
  /**
   * LES TROIS PORTES D'ENTRÉE DU LEVÉ — D.926, D.927, D.930.
   *
   * ⚠️⚠️ ELLES EXISTAIENT EN SERVICE ET N'ÉTAIENT ATTEIGNABLES PAR AUCUN ÉCRAN.
   * Mesuré le 23/09/2026, après la mise en service : `/imports/sig`, `/cogo`
   * et `/terrain` répondaient en production, et
   * `grep -rl "imports/sig\|cogo/derouler\|terrain/synchroniser" frontend/src`
   * rendait **ZÉRO**. Le fondateur l'a dit en une phrase : « un géomètre ne
   * peut pas encore déposer son .dxf en cliquant ».
   *
   * C'est le motif « présent et inatteignable », déjà payé deux fois ce
   * mois-ci — `verifier-emprise`, puis `exports/parcelles`. Une route qui
   * marche et que personne ne peut atteindre n'existe pas pour l'agent.
   *
   * ⚠️ ELLES SONT CONTIGUËS À « Géomètre », ET C'EST LA RÈGLE DU RAIL : les
   * groupes se forment par contiguïté (§7), on ajoute au bon endroit, on ne
   * déplace rien. Les trois sont la MÊME question vue de trois côtés — par
   * quel chemin une mesure de terrain entre au registre : un fichier, un acte
   * ancien, une tablette revenue de brousse.
   */
  { cle: 'imports',     libelle: 'Import SIG',       icone: 'import',   etat: 'disponible', ressource: 'cad.releve' },
  { cle: 'cogo',        libelle: 'Cheminement',      icone: 'cheminement', etat: 'disponible', ressource: 'cad.releve' },
  { cle: 'terrain',     libelle: 'Terrain',          icone: 'terrain',  etat: 'disponible', ressource: 'cad.borne' },
  /**
   * §J.3-J.4 — CE QUI SORT DU REGISTRE, et c'est le quatrième côté de la même
   * question. Les trois précédentes disent par quel chemin une mesure ENTRE :
   * un fichier, un acte ancien, une tablette. Celle-ci dit par quel chemin le
   * registre SE LAISSE LIRE d'un SIG tiers — QGIS, ArcGIS — sans qu'une copie
   * en circule.
   *
   * ⚠️ ELLE N'EST PAS UN CLIENT WMS. Rien de cet écran ne dessine une carte :
   * l'atelier en a déjà une, servie par tuiles vectorielles indexées. Il
   * ADMINISTRE le service — quelles couches, pour quelle habilitation, à quelle
   * adresse. Avant lui, rien dans l'application ne disait que ce service
   * existait, alors qu'il tourne en production.
   *
   * ⚠️ `ressource: 'cad.parcelle'` — c'est la couche la plus ouverte du
   * catalogue OGC. Exiger davantage cacherait l'écran à des rôles qui reçoivent
   * pourtant des couches, et personne ne comprendrait pourquoi son QGIS voit ce
   * que l'atelier lui refuse de nommer.
   */
  { cle: 'ogc',         libelle: 'Services OGC',     icone: 'ogc',      etat: 'disponible', ressource: 'cad.parcelle' },
  /**
   * §19 — LE CENTRE DE CONTRÔLE DES ANOMALIES. Placé après Géomètre : c'est la
   * suite de son travail, et le point d'entrée de qui répare le registre.
   *
   * ⚠️ IL EXISTAIT DÉJÀ UN COMPTEUR SANS ÉCRAN. `modules/modes.ts` publie la
   * tuile « Contrôles de cohérence non traités » depuis les modes métier — 76
   * anomalies ouvertes au 12/09/2026, dont 16 bloquantes — et AUCUNE route ne
   * permettait de les lire, encore moins d'agir. Un compteur qui monte et rien
   * pour le faire descendre : l'agent apprend qu'il y a un problème et n'a
   * aucune porte.
   *
   * `ressource` est celle que le service exige RÉELLEMENT
   * (`exiger(..., 'cad.controle', 'lire')`) — la recopier de mémoire ferait
   * diverger l'écran et la garde, comme cela s'est produit ailleurs.
   */
  { cle: 'anomalies',   libelle: 'Anomalies',        icone: 'anomalies', etat: 'disponible', ressource: 'cad.controle' },
  // §10-§11 — le marché. `lien.transaction` gouverne la lecture PAR PALIERS :
  // sensibilité 1 = agrégats seuls, 2 et plus = le détail des ventes. La
  // rubrique s'ouvre donc dès le niveau 1 — un citoyen voit son secteur, pas
  // les actes de ses voisins.
  { cle: 'immobilier',  libelle: 'Immobilier',       icone: 'immobilier', etat: 'disponible', ressource: 'lien.transaction' },
  { cle: 'coproprietes', libelle: 'Copropriétés',    icone: 'coproprietes', etat: 'disponible', ressource: 'cop.copropriete' },
  { cle: 'fiscal',      libelle: 'Fiscal',           icone: 'fiscal', etat: 'disponible', ressource: 'lien.objet_fiscal' },
  // §16 — la vue PUBLIQUE, embarquée ici pour qu'un agent voie ce qu'un citoyen
  // voit. Sans `ressource` : la vérification est anonyme par construction, et
  // exiger une permission pour REGARDER la page publique n'aurait aucun sens.
  { cle: 'verify',      libelle: 'Verify (vue publique)', icone: 'verify', etat: 'disponible' },
  // D.432 — Mowibo Courrier EST un module de Cadastral, pas une application a
  // cote : l'agent n'en sort jamais pour ecrire. `ressource` est celle que le
  // service exige reellement (`exiger(..., 'fon.affaire', 'lire')`) — la
  // recopier de memoire ferait divergerl'ecran et la garde.
  { cle: 'courrier',    libelle: 'Courrier',         icone: 'sceau', etat: 'disponible', ressource: 'fon.affaire' },
  /**
   * §20-§25 — OUVERTE SUR CE QUI EXISTE VRAIMENT : le terrain et ses
   * contraintes (étapes 1 et 2 des onze du §21). Le moteur de génération
   * urbaine n'est toujours pas écrit, et l'écran l'annonce EN TÊTE.
   *
   * ⚠️ POURQUOI ELLE N'EST PLUS `non_construit`. Laisser une tuile désactivée
   * alors qu'elle rend un service réel cacherait du travail qui fonctionne —
   * l'inverse exact du défaut que cet état combat. Ce qui doit rester vrai,
   * c'est que personne ne croie y trouver les neuf autres étapes : c'est le
   * rôle de l'avertissement de l'écran, pas d'un bouton grisé.
   */
  { cle: 'prospective', libelle: 'Prospective',      icone: 'prospective', etat: 'disponible',
    ressource: 'ter.division' },
  { cle: 'jumeau',      libelle: 'Jumeau 3D / 4D',   icone: 'jumeau', etat: 'non_construit', manque: 'aucune donnée de hauteur : cad.batiment.hauteur_m est vide sur 1 744 535 lignes' },
  /**
   * §19 · §31-§36 · §37 — bâtie le 10/10/2026 (migration 258,
   * src/domaine/intelligence-territoriale.ts).
   *
   * ⚠️ CE QUI EST BÂTI N'EST PAS CE QUE §31-§35 DEMANDAIENT, et l'écran le
   * DIT. Mesuré avant d'écrire une ligne : les quatre facteurs qui décrivent
   * le métier du cadastre — titres, renseignement, revendication, parcellaire
   * levé — valent ZÉRO sur les 462 divisions, et le marché vaut 24
   * transactions pour le pays. Un « opportunity score » aurait donc classé
   * 462 territoires sur de la densité de bâti importée d'OSM, habillée en
   * intelligence territoriale. Ce qui est servi : l'observatoire des faits
   * avec leur population, une note de maturité cadastrale calculée UNIQUEMENT
   * sur les facteurs ayant de la matière, et l'aveu des cinq capacités sans
   * matière — rendu par l'API, affiché à l'écran, pas caché dans un
   * commentaire.
   */
  { cle: 'investment',  libelle: 'Intelligence',     icone: 'investment', etat: 'disponible',
    ressource: 'ter.division' },
  // §45 — l'enrôlement. Placé en dernier : on l'ouvre rarement, mais sans lui
  // aucun agent n'existe, et les premiers comptes du registre avaient dû être
  // écrits à la main en SQL.
  { cle: 'comptes',     libelle: 'Comptes',          icone: 'comptes', etat: 'disponible', ressource: 'sec.compte' },
  /**
   * D.968 — L'ATELIER SIG : la PAGE du métier d'édition. « Le mode édition
   * n'est pas ouvert à tout le monde : ceux qui font de l'édition ont leur
   * propre page » — le fondateur. Tracer une emprise, corriger un contour,
   * et le CHANTIER MITOYEN : déplacer une borne partagée déplace tous les
   * fonds qui la portent (moteur src/partage/topologie-partagee.ts, route
   * PATCH /parcelles/emprises — transaction solidaire). Servie aux porteurs
   * de cad.parcelle : creer OU modifier — les permissions des routes mêmes.
   */
  { cle: 'atelier',     libelle: 'Atelier SIG',      icone: 'atelier', etat: 'disponible',
    ressource: 'cad.parcelle', operations: ['creer', 'modifier'] },
];

export const CLES_RUBRIQUES: string[] = RUBRIQUES.map((r) => r.cle);

/** `undefined` pour une clé inconnue — l'appelant DOIT traiter ce cas, jamais
 *  le sauter : une rubrique déclarée en base et absente d'ici est une erreur
 *  de configuration, et un rail silencieusement incomplet est indiscernable
 *  d'un rail correct. */
export const rubriqueParCle = (cle: string): Rubrique | undefined =>
  RUBRIQUES.find((r) => r.cle === cle);
